import React from 'react';
import { GrowthCategory } from '../types';
import { Info } from 'lucide-react';

interface GrowthScoreGaugeProps {
  score: number;
  factors?: {
    rankingVelocity: number;
    reviewGrowth: number;
    ratingMomentum: number;
    categoryMovement: number;
    countryExpansion: number;
    updateActivity: number;
  };
  showBreakdown?: boolean;
}

export function getGrowthClassification(score: number): { label: GrowthCategory; color: string; bg: string } {
  if (score >= 91) return { label: 'Breakout', color: 'text-sky-500', bg: 'bg-sky-50 text-sky-700 border-sky-200' };
  if (score >= 81) return { label: 'Fast Growing', color: 'text-emerald-500', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  if (score >= 61) return { label: 'Growing', color: 'text-blue-500', bg: 'bg-blue-50 text-blue-700 border-blue-200' };
  if (score >= 31) return { label: 'Stable', color: 'text-amber-500', bg: 'bg-amber-50 text-amber-700 border-amber-200' };
  return { label: 'Low', color: 'text-slate-400', bg: 'bg-slate-50 text-slate-700 border-slate-200' };
}

export const GrowthScoreGauge: React.FC<GrowthScoreGaugeProps> = ({
  score,
  factors,
  showBreakdown = true
}) => {
  const { label, color, bg } = getGrowthClassification(score);

  // SVG Gauge calculations
  const radius = 54;
  const strokeWidth = 9;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Proprietary Metric</span>
          <h3 className="text-lg font-bold text-slate-900 tracking-tight flex items-center gap-1.5">
            <span>Growth Score</span>
            <Info className="w-3.5 h-3.5 text-slate-400 inline-block cursor-help" strokeWidth={2} />
          </h3>
        </div>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${bg}`}>
          {label}
        </span>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-6 py-2">
        {/* Radial Gauge */}
        <div className="relative flex items-center justify-center flex-shrink-0">
          <svg className="w-36 h-36 transform -rotate-90" viewBox="0 0 130 130">
            {/* Background Track */}
            <circle
              cx="65"
              cy="65"
              r={radius}
              stroke="currentColor"
              strokeWidth={strokeWidth}
              className="text-slate-100 fill-none"
            />
            {/* Active Arc */}
            <circle
              cx="65"
              cy="65"
              r={radius}
              stroke="url(#growthGradient)"
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              className="fill-none transition-all duration-1000 ease-out"
            />
            <defs>
              <linearGradient id="growthGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#1177ff" />
                <stop offset="60%" stopColor="#38bdf8" />
                <stop offset="100%" stopColor="#10b981" />
              </linearGradient>
            </defs>
          </svg>

          {/* Centered Score */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="text-4xl font-extrabold tracking-tight text-slate-900">
              {score}
            </span>
            <span className="text-xs font-medium text-slate-400">/ 100</span>
          </div>
        </div>

        {/* Breakdown Explanation */}
        <div className="flex-1 w-full">
          <div className="text-sm text-slate-600 mb-3 leading-relaxed">
            {score >= 90
              ? 'Extreme market velocity. Outperforming 98% of category apps with aggressive ranking spikes.'
              : score >= 80
              ? 'High market velocity. Gaining active momentum across global storefronts.'
              : score >= 60
              ? 'Healthy organic growth pace with steady review and install volume.'
              : 'Steady baseline performance with predictable ranking distribution.'}
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="p-2 rounded-xl bg-slate-50 border border-slate-100">
              <span className="text-slate-400 block">Class</span>
              <strong className="text-slate-800">{label}</strong>
            </div>
            <div className="p-2 rounded-xl bg-slate-50 border border-slate-100">
              <span className="text-slate-400 block">Percentile</span>
              <strong className="text-slate-800">Top {Math.max(1, 100 - score)}%</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Factor Breakdown Bars */}
      {showBreakdown && factors && (
        <div className="mt-6 pt-5 border-t border-slate-100">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">
            Factor Weight Breakdown
          </h4>
          <div className="space-y-2.5">
            {[
              { label: 'Ranking Velocity', value: factors.rankingVelocity },
              { label: 'Review Growth & Velocity', value: factors.reviewGrowth },
              { label: 'Rating Momentum', value: factors.ratingMomentum },
              { label: 'Category Movement', value: factors.categoryMovement },
              { label: 'Country Expansion', value: factors.countryExpansion },
              { label: 'Update Frequency & Cadence', value: factors.updateActivity }
            ].map((f, idx) => (
              <div key={idx} className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600 font-medium">{f.label}</span>
                  <span className="text-slate-900 font-semibold">{f.value}%</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-blue-600 to-sky-400 h-1.5 rounded-full transition-all duration-700"
                    style={{ width: `${f.value}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
